import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CostCentersService } from '../../../../services/cost-centers.service';
import { StatusService } from '../../../../services/status.service';
import { StaffService } from '../../../../services/staff.service';
import { DepartmentService } from '../../../../services/department.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { CostCenter } from '../../../../models/CostCenter';
import { Status } from '../../../../models/Status';
import { StaffMember } from '../../../../models/StaffMember';
import { Department } from '../../../../models/Department';

@Component({
  selector: 'app-cost-center-form',
  templateUrl: './cost-center-form.component.html',
  styleUrls: ['./cost-center-form.component.css']
})
export class CostCenterFormComponent implements OnInit {
  formStep1: FormGroup;
  formStep2: FormGroup;
  formStep3: FormGroup;
  currentStep = 1;
  availableTabs = [
    { id: 'step1', label: 'Identificação', icon: 'fas fa-info-circle', required: true, completed: false },
    { id: 'step2', label: 'Organização', icon: 'fas fa-sitemap', required: false, completed: false },
    { id: 'step3', label: 'Status e Observações', icon: 'fas fa-cog', required: false, completed: false }
  ];
  activeTab = 'step1';
  costCenterId?: number;
  isSubmitting = false;
  isCheckingCode = false;
  codeExists = false;
  isValidatingParent = false;
  parentValidationError: string | null = null;
  
  // Opções para seleção
  parentCostCenters: CostCenter[] = [];
  costCenterTypes = [
    'Receita',
    'Despesa',
    'Investimento',
    'Operacional',
    'Administrativo',
    'Comercial',
    'Financeiro',
    'Marketing',
    'RH',
    'TI',
    'Produção',
    'Logística',
    'Manutenção',
    'Vendas',
    'Suprimentos',
    'Qualidade',
    'Jurídico',
    'Tributário',
    'Imobilizado',
    'Outros'
  ];
  
  loadingStatuses = false;
  statusOptions: Status[] = [];
  staffMembers: StaffMember[] = [];
  loadingStaff = false;
  departments: Department[] = [];
  loadingDepartments = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private costCentersService: CostCentersService,
    private statusService: StatusService,
    private staffService: StaffService,
    private departmentService: DepartmentService,
    private errorService: ErrorService
  ) {
    this.formStep1 = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(20)]],
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      description: ['', Validators.maxLength(500)],
      type: ['Despesa', Validators.required],
      status: ['Ativo', Validators.required]
    });

    this.formStep2 = this.fb.group({
      parentId: [null],
      departmentId: [null as number | null],
      department: [''],
      responsibleStaffId: [null as number | null],
      budget: ['', [this.currencyValidator()]],
      actualCost: ['', [this.currencyValidator()]],
      startDate: [''],
      endDate: ['']
    });

    this.formStep3 = this.fb.group({
      notes: ['', Validators.maxLength(1000)],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadParentCostCenters();
    this.loadCostCenterStatuses();
    this.loadStaffMembers();
    this.loadDepartments();

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.costCenterId = Number(idParam);
      // Edição: carrega listas antes do centro para pré-selecionar departamento/responsável
      this.loadLookupsThenCostCenter();
    }

    // Watch code changes for validation
    this.formStep1.get('code')?.valueChanges.subscribe((code: string) => {
      if (code && code.length >= 2) {
        this.checkCodeExists(code);
      }
    });

    // Watch parent changes for circular reference validation
    this.formStep2.get('parentId')?.valueChanges.subscribe((parentId: number) => {
      if (parentId && this.costCenterId) {
        this.validateParentSelection(parentId);
      } else {
        this.parentValidationError = null;
      }
    });

    this.syncTabFromStep();
    this.updateTabsCompletionStatus();
  }

  loadParentCostCenters(): void {
    this.costCentersService.getActive().subscribe({
      next: result => {
        this.parentCostCenters = (result.items || []).filter((c: any) => 
          !this.costCenterId || c.id !== this.costCenterId
        );
      },
      error: _ => {
        this.parentCostCenters = [];
      }
    });
  }

  private loadLookupsThenCostCenter(): void {
    this.loadingDepartments = true;
    this.loadingStaff = true;
    this.departmentService.getAllDepartments().subscribe({
      next: (deptList) => {
        this.departments = [...(deptList || [])].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'pt-BR')
        );
        this.loadingDepartments = false;
        this.staffService.getStaff().subscribe({
          next: (staffList) => {
            this.staffMembers = [...(staffList || [])].sort((a, b) =>
              (a.FullName || '').localeCompare(b.FullName || '', 'pt-BR')
            );
            this.loadingStaff = false;
            this.loadCostCenter();
          },
          error: () => {
            this.staffMembers = [];
            this.loadingStaff = false;
            this.loadCostCenter();
          }
        });
      },
      error: () => {
        this.departments = [];
        this.loadingDepartments = false;
        this.staffService.getStaff().subscribe({
          next: (staffList) => {
            this.staffMembers = [...(staffList || [])];
            this.loadingStaff = false;
            this.loadCostCenter();
          },
          error: () => {
            this.staffMembers = [];
            this.loadingStaff = false;
            this.loadCostCenter();
          }
        });
      }
    });
  }

  loadDepartments(): void {
    this.loadingDepartments = true;
    this.departmentService.getAllDepartments().subscribe({
      next: (list) => {
        this.departments = [...(list || [])].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'pt-BR')
        );
        this.loadingDepartments = false;
        this.syncDepartmentFromLoadedCenter();
      },
      error: () => {
        this.departments = [];
        this.loadingDepartments = false;
      }
    });
  }

  loadStaffMembers(): void {
    this.loadingStaff = true;
    this.staffService.getStaff().subscribe({
      next: (list) => {
        this.staffMembers = [...(list || [])].sort((a, b) =>
          (a.FullName || '').localeCompare(b.FullName || '', 'pt-BR')
        );
        this.loadingStaff = false;
        this.syncResponsibleStaffFromLoadedCenter();
      },
      error: () => {
        this.staffMembers = [];
        this.loadingStaff = false;
      }
    });
  }

  /** Modo edição: orçamento e custo real não são alterados por esta tela. */
  get isEditMode(): boolean {
    return !!this.costCenterId;
  }

  private applyEditFieldLocks(): void {
    if (!this.isEditMode) {
      return;
    }
    this.formStep2.get('budget')?.disable({ emitEvent: false });
    this.formStep2.get('actualCost')?.disable({ emitEvent: false });
  }

  private syncResponsibleStaffFromLoadedCenter(): void {
    const managerId = this.loadedCenter?.managerStaffId;
    if (managerId != null && managerId > 0) {
      this.formStep2.patchValue({ responsibleStaffId: managerId }, { emitEvent: false });
      return;
    }
    const name = (this.loadedResponsibleName || '').trim();
    if (!name || !this.staffMembers.length) {
      return;
    }
    const match = this.staffMembers.find(
      (s) => (s.FullName || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (match?.Id) {
      this.formStep2.patchValue({ responsibleStaffId: Number(match.Id) }, { emitEvent: false });
    }
  }

  private loadedResponsibleName = '';
  private loadedDepartmentName = '';
  private loadedCenter: CostCenter | null = null;

  private syncDepartmentFromLoadedCenter(): void {
    const name = (this.loadedDepartmentName || '').trim();
    if (!name || !this.departments.length) {
      return;
    }
    const match = this.departments.find(
      (d) => (d.name || '').trim().toLowerCase() === name.toLowerCase()
    );
    if (match?.id) {
      this.formStep2.patchValue(
        { departmentId: Number(match.id), department: match.name || '' },
        { emitEvent: false }
      );
    }
  }

  onDepartmentChange(): void {
    const id = this.formStep2.get('departmentId')?.value;
    const name = this.findDepartmentNameById(id);
    this.formStep2.patchValue({ department: name }, { emitEvent: false });
  }

  loadCostCenter(): void {
    if (!this.costCenterId) return;
    
    this.costCentersService.getById(this.costCenterId).subscribe({
      next: center => {
        this.loadedCenter = center;
        this.loadedResponsibleName = (center.responsible || '').trim();
        this.loadedDepartmentName = (center.department || '').trim();
        const budgetValue = center.monthlyBudget ?? center.budget;

        this.formStep1.patchValue({
          code: center.code,
          name: center.name,
          description: center.description,
          type: center.type,
          status: center.status
        });

        this.formStep2.patchValue({
          parentId: center.parentId != null && center.parentId > 0 ? center.parentId : null,
          budget: budgetValue != null ? this.maskCurrency(String(budgetValue)) : '',
          actualCost: center.actualCost != null ? this.maskCurrency(center.actualCost.toString()) : '',
          responsibleStaffId: null,
          departmentId: null,
          department: '',
          startDate: this.toDateInputValue(center.startDate),
          endDate: this.toDateInputValue(center.endDate)
        });

        this.formStep3.patchValue({
          notes: center.notes,
          isActive: center.isActive
        });

        this.syncResponsibleStaffFromLoadedCenter();
        this.syncDepartmentFromLoadedCenter();
        this.applyEditFieldLocks();
      },
      error: _ => {
        this.router.navigate(['/financial/cost-centers']);
      }
    });
  }

  checkCodeExists(code: string): void {
    if (!code || code.length < 2) return;
    
    this.isCheckingCode = true;
    this.costCentersService.checkCodeExists(code, this.costCenterId).subscribe({
      next: result => {
        this.codeExists = result.exists;
        this.isCheckingCode = false;
        
        const codeControl = this.formStep1.get('code');
        if (this.codeExists && codeControl) {
          codeControl.setErrors({ codeExists: true });
        }
      },
      error: _ => {
        this.isCheckingCode = false;
      }
    });
  }

  validateParentSelection(parentId: number): void {
    if (!this.costCenterId || !parentId) {
      this.parentValidationError = null;
      return;
    }

    this.isValidatingParent = true;
    this.costCentersService.validateParent(this.costCenterId, parentId).subscribe({
      next: result => {
        this.isValidatingParent = false;
        
        if (result.hasCircularReference) {
          this.parentValidationError = result.message;
          const parentControl = this.formStep2.get('parentId');
          if (parentControl) {
            parentControl.setErrors({ circularReference: true });
          }
        } else {
          this.parentValidationError = null;
          const parentControl = this.formStep2.get('parentId');
          if (parentControl && parentControl.hasError('circularReference')) {
            parentControl.setErrors(null);
          }
        }
      },
      error: _ => {
        this.isValidatingParent = false;
        this.parentValidationError = null;
      }
    });
  }

  nextStep(): void { 
    if (this.currentStep < 3) {
      this.currentStep++;
      this.syncTabFromStep();
      this.updateTabsCompletionStatus();
    }
  }
  
  prevStep(): void { 
    if (this.currentStep > 1) {
      this.currentStep--;
      this.syncTabFromStep();
    }
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    this.syncStepFromTab();
  }

  save(): void {
    const isStep1Valid = this.formStep1.valid;
    const isStep2Valid = this.formStep2.valid;
    const isStep3Valid = this.formStep3.valid;
    
    if (!isStep1Valid || !isStep2Valid || !isStep3Valid || this.codeExists || this.parentValidationError) {
      this.markFormGroupTouched();
      const errorMessages = this.collectValidationErrors();
      this.errorService.showErrorMessage(errorMessages);
      return;
    }

    this.isSubmitting = true;
    const step1 = this.formStep1.getRawValue();
    const step2 = this.formStep2.getRawValue();
    const step3 = this.formStep3.getRawValue();

    const managerStaffId = this.toNumberOrUndefined(step2.responsibleStaffId);
    const monthlyBudget = this.normalizeCurrency(step2.budget);

    const parentCostCenterId = this.toNumberOrUndefined(step2.parentId);

    const payload: Record<string, unknown> = {
      code: step1.code,
      name: step1.name,
      description: step1.description,
      type: step1.type,
      status: step1.status,
      department:
        (step2.department || '').trim() ||
        this.resolveDepartmentName(step2.departmentId),
      parentCostCenterId: parentCostCenterId ?? null,
      managerStaffId,
      category: this.loadedCenter?.category ?? '',
      branch: this.loadedCenter?.branch ?? '',
      isActive: step3.isActive,
      acceptsCosts: this.loadedCenter?.acceptsCosts ?? true,
      acceptsRevenues: this.loadedCenter?.acceptsRevenues ?? false,
      notes: step3.notes ?? '',
      startDate: step2.startDate || null,
      endDate: step2.endDate || null
    };

    if (!this.costCenterId) {
      payload['monthlyBudget'] = monthlyBudget;
      payload['annualBudget'] =
        monthlyBudget != null ? Math.round(monthlyBudget * 12 * 100) / 100 : undefined;
      payload['parentCostCenterId'] = parentCostCenterId ?? null;
    }

    if (this.costCenterId) {
      this.costCentersService.update(this.costCenterId, payload as any).subscribe({
        next: _ => {
          this.isSubmitting = false;
          this.router.navigate(['/financial/cost-centers']);
        },
        error: _ => {
          this.isSubmitting = false;
        }
      });
    } else {
      this.costCentersService.create(payload as any).subscribe({
        next: _ => {
          this.isSubmitting = false;
          this.router.navigate(['/financial/cost-centers']);
        },
        error: _ => {
          this.isSubmitting = false;
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/financial/cost-centers']);
  }

  private markFormGroupTouched(): void {
    Object.keys(this.formStep1.controls).forEach(key => {
      this.formStep1.get(key)?.markAsTouched();
    });
    Object.keys(this.formStep2.controls).forEach(key => {
      this.formStep2.get(key)?.markAsTouched();
    });
    Object.keys(this.formStep3.controls).forEach(key => {
      this.formStep3.get(key)?.markAsTouched();
    });
  }

  /** Coleta mensagens de erro de todos os campos para exibir no popup ao tentar salvar com formulário inválido. */
  private collectValidationErrors(): string {
    const messages: string[] = [];
    const groups = [
      { group: this.formStep1, keys: ['code', 'name', 'description', 'type', 'status'] },
      { group: this.formStep2, keys: ['parentId', 'departmentId', 'responsibleStaffId', 'budget', 'actualCost', 'startDate', 'endDate'] },
      { group: this.formStep3, keys: ['notes'] }
    ];
    for (const { group, keys } of groups) {
      for (const key of keys) {
        const control = group.get(key);
        if (control?.invalid && control?.errors) {
          const msg = this.getFieldError(key);
          if (msg) messages.push(msg);
        }
      }
    }
    if (this.codeExists) messages.push('Este código já está sendo usado por outro centro de custo.');
    if (this.parentValidationError) messages.push(this.parentValidationError);
    return messages.length ? messages.join(' • ') : 'Verifique os campos do formulário.';
  }

  // Getters para validação no template
  get code() { return this.formStep1.get('code'); }
  get name() { return this.formStep1.get('name'); }
  get description() { return this.formStep1.get('description'); }
  get type() { return this.formStep1.get('type'); }
  get status() { return this.formStep1.get('status'); }
  get parentId() { return this.formStep2.get('parentId'); }
  get budget() { return this.formStep2.get('budget'); }
  get actualCost() { return this.formStep2.get('actualCost'); }
  get responsibleStaffId() { return this.formStep2.get('responsibleStaffId'); }
  get departmentId() { return this.formStep2.get('departmentId'); }
  get notes() { return this.formStep3.get('notes'); }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.formStep1.get(fieldName) || this.formStep2.get(fieldName) || this.formStep3.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  /** Verdadeiro quando o campo é válido e já foi tocado ou alterado (para exibir borda verde só após interação). */
  isFieldValidAndTouched(fieldName: string): boolean {
    const field = this.formStep1.get(fieldName) || this.formStep2.get(fieldName) || this.formStep3.get(fieldName);
    return !!(field && field.valid && (field.touched || field.dirty));
  }

  getFieldError(fieldName: string): string {
    const field = this.formStep1.get(fieldName) || this.formStep2.get(fieldName) || this.formStep3.get(fieldName);
    if (!field || !field.errors || !field.touched) return '';

    const errors = field.errors;
    
    if (errors['required']) return `${this.getFieldLabel(fieldName)} é obrigatório`;
    if (errors['minlength']) return `${this.getFieldLabel(fieldName)} deve ter pelo menos ${errors['minlength'].requiredLength} caracteres`;
    if (errors['maxlength']) return `${this.getFieldLabel(fieldName)} deve ter no máximo ${errors['maxlength'].requiredLength} caracteres`;
    if (errors['min']) return `${this.getFieldLabel(fieldName)} deve ser maior ou igual a ${errors['min'].min}`;
    if (errors['codeExists']) return 'Este código já está sendo usado por outro centro de custo';
    if (errors['circularReference']) return this.parentValidationError || 'Esta seleção criaria uma referência circular na hierarquia';
    if (errors['invalidCurrency']) return `${this.getFieldLabel(fieldName)} deve ser um valor válido`;
    
    return 'Campo inválido';
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      code: 'Código',
      name: 'Nome',
      description: 'Descrição',
      type: 'Tipo',
      status: 'Status',
      parentId: 'Centro de Custo Pai',
      budget: 'Orçamento',
      actualCost: 'Custo Real',
      responsibleStaffId: 'Responsável',
      departmentId: 'Departamento',
      notes: 'Observações'
    };
    return labels[fieldName] || fieldName;
  }

  // Máscaras de valor
  onBudgetInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCurrency(input.value || '');
    this.formStep2.get('budget')?.setValue(masked, { emitEvent: false });
  }

  onActualCostInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCurrency(input.value || '');
    this.formStep2.get('actualCost')?.setValue(masked, { emitEvent: false });
  }

  loadCostCenterStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('CostCenter', true)
      .subscribe({
        next: (statuses: Status[]) => {
          this.statusOptions = statuses;
          this.loadingStatuses = false;
        },
        error: (err) => {
          console.error('Erro ao carregar status de centros de custo:', err);
          this.loadingStatuses = false;
          // Fallback para status básicos em caso de erro
          this.statusOptions = [
            { id: 1, name: 'Ativo', description: 'Centro de custo ativo', entityType: 'CostCenter', isActive: true },
            { id: 2, name: 'Inativo', description: 'Centro de custo inativo', entityType: 'CostCenter', isActive: true },
            { id: 3, name: 'Suspenso', description: 'Centro de custo suspenso', entityType: 'CostCenter', isActive: true }
          ] as Status[];
        }
      });
  }

  private maskCurrency(value: string): string {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';

    // Valor vindo do backend (número puro, ex: "1500.5") — formata como reais
    const isLoadValue = !trimmed.includes('R$');
    if (isLoadValue) {
      const normalized = trimmed.replace(',', '.');
      const num = parseFloat(normalized.replace(/[^\d.-]/g, ''));
      if (!isNaN(num)) {
        return num.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
      }
    }

    // Digitação do usuário: dígitos = centavos (evita valor “pulando” e zeros crescendo)
    const digits = trimmed.replace(/\D+/g, '');
    if (!digits) return '';
    const cents = parseInt(digits, 10);
    if (isNaN(cents)) return '';
    const number = cents / 100;

    return number.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  private normalizeCurrency(value: string | undefined): number | undefined {
    if (!value) return undefined;
    
    // Remove símbolos de moeda e espaços
    let cleanValue = value.replace(/[R$\s]/g, '');
    
    // Se tem vírgula, é formato brasileiro (1.234,56)
    if (cleanValue.includes(',')) {
      // Remove pontos de milhares e substitui vírgula por ponto
      cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
    }
    
    const number = parseFloat(cleanValue);
    return isNaN(number) ? undefined : number;
  }

  private toNumberOrUndefined(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '' || value === 'null') {
      return undefined;
    }
    const num = Number(value);
    return Number.isNaN(num) ? undefined : num;
  }

  private resolveResponsibleName(staffId: unknown): string {
    const id = this.toNumberOrUndefined(staffId);
    if (id == null) {
      return (this.loadedResponsibleName || '').trim();
    }
    const staff = this.staffMembers.find((s) => Number(s.Id) === id);
    return (staff?.FullName || this.loadedResponsibleName || '').trim();
  }

  private toDateInputValue(value?: string | null): string {
    if (!value) {
      return '';
    }
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return s.substring(0, 10);
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().substring(0, 10);
  }

  private findDepartmentNameById(departmentId: unknown): string {
    const id = this.toNumberOrUndefined(departmentId);
    if (id == null) {
      return '';
    }
    const dept = this.departments.find(
      (d) => Number(d.id) === id || String(d.id) === String(departmentId)
    );
    return (dept?.name || '').trim();
  }

  private resolveDepartmentName(departmentId: unknown): string {
    const fromId = this.findDepartmentNameById(departmentId);
    if (fromId) {
      return fromId;
    }
    return (this.loadedDepartmentName || '').trim();
  }

  private currencyValidator() {
    return (control: any) => {
      if (!control.value) return null;
      
      const normalized = this.normalizeCurrency(control.value);
      if (normalized === undefined || normalized < 0) {
        return { invalidCurrency: true };
      }
      
      return null;
    };
  }

  private syncTabFromStep(): void {
    this.activeTab = `step${this.currentStep}`;
  }

  private syncStepFromTab(): void {
    const parsed = Number(this.activeTab.replace('step', ''));
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 3) {
      this.currentStep = parsed;
    }
  }

  private updateTabsCompletionStatus(): void {
    this.availableTabs.forEach((tab, index) => {
      tab.completed = this.currentStep > index + 1;
    });
  }
}
